import { useContext, useEffect } from 'react';
import { PageHeadContext } from '../head/pageHead';
import { serializeStructuredData } from '../head/structuredData';

const DESCRIPTION_SELECTOR = 'meta[name="description"]';
const STRUCTURED_DATA_ID = 'structured-data';

function upsertDescription(description: string) {
    const existing = document.head.querySelector<HTMLMetaElement>(DESCRIPTION_SELECTOR);
    if (existing) {
        existing.content = description;
        return;
    }

    const meta = document.createElement('meta');
    meta.name = 'description';
    meta.content = description;
    document.head.appendChild(meta);
}

function upsertStructuredData(structuredData: object[] | undefined) {
    const existing = document.getElementById(STRUCTURED_DATA_ID);
    if (!structuredData) {
        existing?.remove();
        return;
    }
    const script = existing ?? document.createElement('script');
    script.id = STRUCTURED_DATA_ID;
    script.setAttribute('type', 'application/ld+json');
    script.textContent = serializeStructuredData(structuredData);
    if (!existing) document.head.appendChild(script);
}

/**
 * Declares the title, description and optional structured data of the current page. On the
 * server the values are collected into the PageHead the entry provides, so they end up in the
 * rendered head; in the browser they are applied to the document after navigation.
 */
export function usePageMeta(title: string, description: string, structuredData?: object[]) {
    const serverHead = useContext(PageHeadContext);
    if (serverHead) {
        serverHead.title = title;
        serverHead.description = description;
        serverHead.structuredData = structuredData;
    }

    useEffect(() => {
        document.title = title;
        upsertDescription(description);
        upsertStructuredData(structuredData);
    }, [title, description, structuredData]);
}
